/* global SideDrawer */

if (!customElements.get('facet-filters')) {
  class FacetFilters extends SideDrawer {
    connectedCallback() {
      this.init();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.historyChangeHandler);

      if (this.breakpointChangeHandler) {
        window.removeEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }
    }

    init() {
      this.filteringEnabled = this.dataset.filtering === 'true';
      this.sortingEnabled = this.dataset.sorting === 'true';
      this.form = document.getElementById('facets');
      this.results = document.getElementById('filter-results');
      this.expanded = [];
      this.filterChangeTimeout = null;

      this.handleBreakpointChange();
      this.addElements();
      this.addListeners();
      this.applyFilterDependencies();
      this.sortApparelSizeFilter();

      this.historyChangeHandler = this.historyChangeHandler || this.handleHistoryChange.bind(this);
      window.addEventListener('popstate', this.historyChangeHandler);
    }

    addElements() {
      if (this.filteringEnabled) {
        this.filters = this.querySelector('.facets__filters');
        this.activeFilters = this.querySelector('.facets__active-filters');
        this.activeFiltersList = this.querySelector('.active-filters');
        this.activeFiltersHeader = this.querySelector('.active-filters-header');
        this.footer = this.querySelector('.facets__footer');
      }

      if (this.sortingEnabled) {
        this.mobileSortByOptions = this.querySelectorAll('.js-drawer-sort-by');
        this.desktopSortBy = document.querySelector('.products-toolbar__sort');
      }
    }

    addListeners() {
      if (this.filteringEnabled) {
        this.breakpointChangeHandler = this.breakpointChangeHandler
          || this.handleBreakpointChange.bind(this);
        this.filters.addEventListener('click', this.handleFiltersClick.bind(this));
        this.filters.addEventListener('input', this.handleFilterChange.bind(this));
        this.filters.addEventListener('change', this.handleFilterChange.bind(this));
        this.activeFilters.addEventListener('click', this.handleActiveFiltersClick.bind(this));
        window.addEventListener('on:breakpoint-change', this.breakpointChangeHandler);
      }

      if (this.sortingEnabled) {
        this.desktopSortBy.addEventListener('change', this.handleFilterChange.bind(this));
      }
    }

    /**
     * Handles viewport breakpoint changes.
     */
    handleBreakpointChange() {
      if (theme.mediaMatches.lg) {
        this.setAttribute('open', '');
        this.setAttribute('aria-hidden', 'false');
        this.removeAttribute('aria-modal');
        this.removeAttribute('role');
      } else {
        this.close();
        this.setAttribute('role', 'dialog');
        this.setAttribute('aria-modal', 'true');
        this.setAttribute('aria-hidden', 'true');
        this.hidden = false;
      }
    }

    /**
     * Handles 'input' events on the filters and 'change' events on the sort by dropdown.
     * @param {object} evt - Event object.
     */
    handleFilterChange(evt) {
      // Only allow price 'change' events
      if (evt.type === 'change' && !(evt.target.id?.includes('price-range') || evt.target.id?.includes('sort-by'))) return;

      // Dont reload when typing a price
      if (evt.target.id?.includes('price-range') && evt.constructor.name === 'InputEvent') return;

      const timeoutDelay = 500;

      clearTimeout(this.filterChangeTimeout);

      this.filterChangeTimeout = setTimeout(() => {
        const formData = new FormData(this.form);
        const searchParams = new URLSearchParams(formData);
        const emptyParams = [];

        if (this.sortingEnabled) {
          let currentSortBy = searchParams.get('sort_by');

          // Keep the mobile facets form sync'd with the desktop sort by dropdown
          if (evt.target.tagName === 'CUSTOM-SELECT') {
            this.mobileSortByOptions.forEach((option) => {
              option.checked = option.value === evt.detail.selectedValue;
              currentSortBy = evt.detail.selectedValue;
            });
          }

          // Set the 'sort_by' parameter.
          searchParams.set('sort_by', currentSortBy);
        }

        // Get empty parameters.
        searchParams.forEach((value, key) => {
          if (!value) emptyParams.push(key);
        });

        // Remove empty parameters.
        emptyParams.forEach((key) => {
          searchParams.delete(key);
        });

        this.applyFilters(searchParams.toString(), evt);
      }, timeoutDelay);
    }

    /**
     * Handles 'click' events on the filters.
     * @param {object} evt - Event object.
     */
    handleFiltersClick(evt) {
      const { target } = evt;

      // Filter 'clear' button clicked.
      if (target.matches('.js-clear-filter')) {
        evt.preventDefault();
        this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
      }

      // Filter 'show more' button clicked.
      if (target.matches('.js-show-more')) {
        const filter = target.closest('.filter');
        target.remove();

        filter.querySelectorAll('li').forEach((el) => {
          el.classList.remove('js-hidden');
        });

        if (!this.expanded.includes(filter.id)) {
          this.expanded.push(filter.id);
        }
      }
    }

    /**
     * Handles 'click' events on the active filters.
     * @param {object} evt - Event object.
     */
    handleActiveFiltersClick(evt) {
      if (evt.target.tagName !== 'A') return;
      evt.preventDefault();
      this.applyFilters(new URL(evt.target.href).searchParams.toString(), evt);
    }

    /**
     * Handles history changes (e.g. back button clicked).
     * @param {object} evt - Event object.
     */
    handleHistoryChange(evt) {
      if (evt.state !== null) {
        let searchParams = '';

        if (evt.state && evt.state.searchParams) {
          ({ searchParams } = evt.state);
        }

        this.applyFilters(searchParams, null, false);
      }
    }

    /**
     * Fetches the filtered/sorted page data and updates the current page.
     * @param {string} searchParams - Filter/sort search parameters.
     * @param {object} evt - Event object.
     * @param {boolean} [updateUrl=true] - Update url with the selected options.
     */
    async applyFilters(searchParams, evt, updateUrl = true) {
      try {
        // Preserve the current element focus
        const activeElementId = document.activeElement.id;

        // Disable infinite scrolling.
        const customPagination = document.querySelector('custom-pagination');
        if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'true';

        // Set loading state.
        this.results.classList.add('is-loading');

        // Disable "Show X results" button until submission is complete.
        const closeBtn = this.querySelector('.js-close-drawer-mob');
        closeBtn.ariaDisabled = 'true';
        closeBtn.classList.add('is-loading');

        // Use Section Rendering API for the request, if possible.
        let fetchUrl = `${window.location.pathname}?${searchParams}`;
        if (this.form.dataset.filterSectionId) {
          fetchUrl += `&section_id=${this.form.dataset.filterSectionId}`;
        }

        // Cancel current fetch request. (Raises an exception)
        if (this.applyFiltersFetchAbortController) {
          this.applyFiltersFetchAbortController.abort('Request changed');
        }
        this.applyFiltersFetchAbortController = new AbortController();

        // Fetch filtered products markup.
        const response = await fetch(fetchUrl, {
          method: 'GET',
          signal: this.applyFiltersFetchAbortController.signal
        });

        if (response.ok) {
          const tmpl = document.createElement('template');
          tmpl.innerHTML = await response.text();

          // Restore UI state.
          this.form.querySelectorAll('details-disclosure > details').forEach((existingFilter) => {
            const target = tmpl.content.getElementById(existingFilter.id);
            if (target) {
              target.open = existingFilter.open;
            }
          });
          tmpl.content.querySelectorAll('#facets details-disclosure > details').forEach((newFilter) => {
            if (this.expanded.includes(newFilter.id)) {
              const hiddenElements = newFilter.querySelectorAll('.js-hidden');
              hiddenElements.forEach((listItem) => {
                listItem.classList.remove('js-hidden');
              });
              newFilter.querySelector('.filter__more')?.remove();
            }
          });

          // Update the filters.
          this.form.innerHTML = tmpl.content.getElementById('facets').innerHTML;

          // Update the label of the mobile filter button
          closeBtn.innerText = tmpl.content.querySelector('.js-close-drawer-mob').innerText;

          // Preserve the CSS class of the results
          const currentResultsUl = this.results.querySelector('ul');
          this.currentResultsClass = currentResultsUl ? this.results.querySelector('ul').getAttribute('class') : this.currentResultsClass;

          // Update the results.
          this.results.innerHTML = tmpl.content.getElementById('filter-results').innerHTML;

          // Set the CSS class of the results to what it was
          const newResultsUl = this.results.querySelector('ul');
          if (newResultsUl && this.currentResultsClass) newResultsUl.setAttribute('class', this.currentResultsClass);

          // Reinitialize re-rendered components.
          this.addElements();
          this.addListeners();
          this.applyFilterDependencies();
          this.sortApparelSizeFilter();

          // Reinitialize any custom pagination
          if (customPagination && customPagination.reload) customPagination.reload();

          // Update the URL.
          if (updateUrl) FacetFilters.updateURL(searchParams);

          // Scroll to the top of the results if needed
          if (this.results.getBoundingClientRect().top < 0) {
            // If the header is sticky, compensate for it when scrolling to elements
            let headerHeight = 0;
            if (document.querySelector('store-header[data-is-sticky="true"]')) {
              headerHeight = Number.parseInt(
                getComputedStyle(this.parentElement)
                  .getPropertyValue('--header-height')
                  .replace('px', ''),
                10
              ) || 0;
            }
            window.scrollTo({
              top: this.results.getBoundingClientRect().top + window.scrollY - headerHeight - 45,
              behavior: 'smooth'
            });
          }

          // Enable the "Show X results" button
          closeBtn.classList.remove('is-loading');
          closeBtn.removeAttribute('aria-disabled');

          // Renable infinite scroll
          if (customPagination) customPagination.dataset.pauseInfiniteScroll = 'false';

          // Focus on the element with the same ID in the new HTML
          if (activeElementId) document.getElementById(activeElementId)?.focus();

          // Broadcast the update for anything else to hook into
          document.dispatchEvent(new CustomEvent('on:facet-filters:updated'), { bubbles: true });
        }
      } catch (error) {
        console.warn(error); // eslint-disable-line
      } finally {
        this.results.classList.remove('is-loading');
      }
    }

    /**
     * Gets the currently active filter values, grouped by filter label.
     * @returns {Object} - Map of filter label to array of active values.
     */
    getActiveFilterValues() {
      const activeValues = {};

      if (!this.form) return activeValues;

      this.form.querySelectorAll('[data-filter-label]').forEach((filterGroup) => {
        const label = filterGroup.dataset.filterLabel;
        const checked = filterGroup.querySelectorAll('input:checked');
        const values = [];

        checked.forEach((input) => {
          const filterLabel = input.closest('li')?.querySelector('.filter__label-text');
          if (filterLabel) {
            values.push(filterLabel.textContent.trim());
          }
        });

        if (values.length) {
          activeValues[label] = values;
        }
      });

      return activeValues;
    }

    /**
     * Sorts apparel size filter values by base size, grouping tall variants
     * after their base, youth sizes together, and slash sizes at the end.
     */
    sortApparelSizeFilter() {
      if (!this.form) return;

      const filterGroup = this.form.querySelector('[data-filter-label="Size"]');
      if (!filterGroup) return;

      const list = filterGroup.querySelector('.filter__size-buttons');
      if (!list) return;

      const items = Array.from(list.children);
      if (items.length < 2) return;

      const baseOrder = ['2XS', 'XS', 'S', 'SM', 'M', 'MD', 'L', 'LG', 'XL', '2XL', '3XL', '4XL', '5XL', '6XL'];

      const getTextFromLi = (li) => (li.querySelector('.filter__size-btn-text') || li.querySelector('.filter__label-text') || li).textContent.trim();

      // Groups: 0=standard+tall, 1=bra sizes, 2=inseam variants, 3=youth, 4=plus sizes, 5=numeric, 6=one-size, 7=slash sizes
      const plusOrder = ['1X', '2X', '3X', '4X', '5X', '6X'];
      const oneSizeValues = ['OSFA', 'OSFM'];
      const braPattern = /^(.+)\s+(A-C|D-DD|D-DDD|DD-DDD|A|B|C|D|DD|DDD)$/;
      const inseamPattern = /^(.+)\s+(\d+IN)$/;
      const plusPattern = /^\d+X$/;
      const numericPattern = /^\d+$/;

      const getSortKey = (text) => {
        const upper = text.toUpperCase();

        // Slash sizes — group 7
        if (upper.includes('/')) return { group: 7, rank: 0, isBase: false, text: upper };

        // One-size — group 6
        if (oneSizeValues.includes(upper)) return { group: 6, rank: oneSizeValues.indexOf(upper), isBase: true, text: upper };

        // Pure numeric sizes — group 5
        if (numericPattern.test(upper)) return { group: 5, rank: parseInt(upper, 10), isBase: true, text: upper };

        // Plus sizes (1X, 2X, 3X) — group 4
        if (plusPattern.test(upper)) {
          const rank = plusOrder.indexOf(upper);
          return { group: 4, rank: rank >= 0 ? rank : plusOrder.length, isBase: true, text: upper };
        }

        // Bra sizes (XS A-C, M D-DD) — group 1
        const braMatch = upper.match(braPattern);
        if (braMatch) {
          const braBase = braMatch[1].trim();
          let rank = baseOrder.length;
          for (let i = 0; i < baseOrder.length; i++) {
            if (braBase === baseOrder[i]) { rank = i; break; }
          }
          return { group: 1, rank, isBase: false, text: upper };
        }

        // Inseam variants (2XS 3IN, L 5IN) — group 2
        const inseamMatch = upper.match(inseamPattern);
        if (inseamMatch) {
          const inseamBase = inseamMatch[1].trim();
          let rank = baseOrder.length;
          for (let i = 0; i < baseOrder.length; i++) {
            if (inseamBase === baseOrder[i]) { rank = i; break; }
          }
          return { group: 2, rank, isBase: false, text: upper };
        }

        // Youth — group 3, strip Y prefix to find base
        const isYouth = upper.startsWith('Y') && upper.length > 1 && !baseOrder.includes(upper);
        const stripped = isYouth ? upper.slice(1) : upper;

        // Map shorthand patterns to their full base (e.g. 2XT2 → 2XL, 3XT2 → 3XL)
        let normalized = stripped;
        const shorthandMatch = normalized.match(/^(\d+)XT(\d*)$/);
        if (shorthandMatch) {
          normalized = shorthandMatch[1] + 'XL' + 'T' + shorthandMatch[2];
        }

        // Extract base size by matching longest known base from the start
        let base = null;
        let suffix = normalized;
        for (let i = 0; i < baseOrder.length; i++) {
          const b = baseOrder[i];
          if (normalized.startsWith(b) && (!base || b.length > base.length)) {
            base = b;
            suffix = normalized.slice(b.length);
          }
        }

        const rank = base ? baseOrder.indexOf(base) : baseOrder.length;

        return {
          group: isYouth ? 3 : 0,
          rank,
          isBase: suffix === '',
          text: upper
        };
      };

      items.sort((a, b) => {
        const ka = getSortKey(getTextFromLi(a));
        const kb = getSortKey(getTextFromLi(b));

        // Sort by group first
        if (ka.group !== kb.group) return ka.group - kb.group;

        // Slash sizes — numeric locale compare
        if (ka.group === 7) return ka.text.localeCompare(kb.text, undefined, { numeric: true, sensitivity: 'base' });

        // Sort by base size rank
        if (ka.rank !== kb.rank) return ka.rank - kb.rank;

        // Base size comes before its variants
        if (ka.isBase !== kb.isBase) return ka.isBase ? -1 : 1;

        // Alphabetical within same base rank
        return ka.text.localeCompare(kb.text, undefined, { numeric: true, sensitivity: 'base' });
      });

      items.forEach((li) => list.appendChild(li));

      // Deduplicate case-insensitive size values (prioritize uppercase)
      const seen = new Map();
      items.forEach((li) => {
        const text = getTextFromLi(li);
        const key = text.toUpperCase();
        if (seen.has(key)) {
          const existing = seen.get(key);
          const existingText = getTextFromLi(existing);
          // Prefer the version that's already uppercase
          let canonical, duplicate;
          if (existingText === key) {
            canonical = existing;
            duplicate = li;
          } else if (text === key) {
            canonical = li;
            duplicate = existing;
            seen.set(key, li);
          } else {
            canonical = existing;
            duplicate = li;
          }

          duplicate.hidden = true;
          const dupeInput = duplicate.querySelector('input[type="checkbox"]');
          const canonicalInput = canonical.querySelector('input[type="checkbox"]');

          // If duplicate was active, activate the canonical
          if (dupeInput?.checked && canonicalInput && !canonicalInput.checked) {
            canonicalInput.checked = true;
          }

          // Sync: when canonical is toggled, mirror to hidden duplicate
          if (canonicalInput && dupeInput) {
            canonicalInput.addEventListener('change', () => {
              dupeInput.checked = canonicalInput.checked;
            });
          }
        } else {
          seen.set(key, li);
        }
      });
    }

    /**
     * Shows/hides filter groups based on dependency rules and active filter selections.
     */
    applyFilterDependencies() {
      const rulesEl = document.getElementById('dependent-filter-rules');
      if (!rulesEl) return;

      let config;
      try {
        config = JSON.parse(rulesEl.textContent);
      } catch (e) {
        return;
      }

      const { rules, collectionHandle, templateSuffix } = config;
      if (!rules || !rules.length) return;

      const activeValues = this.getActiveFilterValues();

      rules.forEach((rule) => {
        const filterGroup = this.form.querySelector(`[data-filter-label="${rule.show}"]`);
        if (!filterGroup) return;

        // Skip visibility logic for specified templates (filter shows by default)
        const skipRule = rule.skipForTemplates && templateSuffix && rule.skipForTemplates.includes(templateSuffix);

        let shouldShow = skipRule;

        // Check primary "when" rule (specific values in a specific filter)
        if (rule.when) {
          const dependencyValues = activeValues[rule.when.filter] || [];
          shouldShow = rule.when.contains.some((v) => dependencyValues.includes(v));
        }

        // Auto-detect from collection handle (word-boundary match to prevent "womens" matching "mens")
        if (!shouldShow && collectionHandle && rule.handleContains) {
          shouldShow = rule.handleContains.some((v) => new RegExp(`(?:^|-)${v}(?:$|-)`, 'i').test(collectionHandle));
        }

        // Check "orWhenAnySelected" — show if any of these filters have any active value
        if (!shouldShow && rule.orWhenAnySelected) {
          shouldShow = rule.orWhenAnySelected.some((label) => (activeValues[label] || []).length > 0);
        }

        filterGroup.closest('details-disclosure').style.display = shouldShow ? '' : 'none';

        // Rename the filter label if specified
        if (rule.rename) {
          const labelEl = filterGroup.querySelector('.font-bold');
          if (labelEl && labelEl.textContent.trim() !== rule.rename) {
            labelEl.textContent = rule.rename;
          }
        }
      });
    }

    /**
     * Updates the url with the current filter/sort parameters.
     * @param {string} searchParams - Filter/sort parameters.
     */
    static updateURL(searchParams) {
      window.history.pushState(
        { searchParams },
        '',
        `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
      );
    }
  }

  customElements.define('facet-filters', FacetFilters);
}
